<script setup>
import {
  computed,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import DataImportsAPI from 'dashboard/api/dataImports';
import { POLL_INTERVAL_MS, isActiveImport } from './importStatus';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import ImportDetailHeader from './components/ImportDetailHeader.vue';
import ImportSummaryTiles from './components/ImportSummaryTiles.vue';
import ImportProgress from './components/ImportProgress.vue';
import ImportErrorsSection from './components/ImportErrorsSection.vue';
import ImportSkipLogsSection from './components/ImportSkipLogsSection.vue';

const { t } = useI18n();
const route = useRoute();

const dataImport = ref(null);
const isLoading = ref(true);
const isRefreshing = ref(false);
const isPolling = ref(false);
const isRetrying = ref(false);
const isAbandoning = ref(false);
const isDownloadingErrorLogs = ref(false);
const isDownloadingSkipLogs = ref(false);
const isChangingSkipLogsType = ref(false);
const selectedSkipLogsType = ref('');
const errorsOpen = ref(true);
const skipLogsOpen = ref(true);
let pollTimer;
let isPageActive = false;
let importRequestVersion = 0;

const hasActiveImport = computed(() => isActiveImport(dataImport.value));

const pageTitle = computed(
  () => dataImport.value?.name || t('DATA_IMPORTS.TABLE.UNNAMED')
);

const stopPolling = () => {
  if (!pollTimer) return;

  window.clearInterval(pollTimer);
  pollTimer = null;
};

const fetchImport = async ({
  showLoader = false,
  manual = false,
  requestedSkipLogsType = selectedSkipLogsType.value,
} = {}) => {
  const requestVersion = importRequestVersion;
  if (showLoader) {
    isLoading.value = true;
  } else if (manual) {
    isRefreshing.value = true;
  }

  try {
    const response = await DataImportsAPI.show(route.params.dataImportId, {
      skip_logs_type: requestedSkipLogsType || undefined,
    });
    if (requestVersion !== importRequestVersion) return;

    dataImport.value = response.data;
    selectedSkipLogsType.value =
      response.data.skip_logs_filters?.selected_source_object_type ||
      requestedSkipLogsType ||
      '';
  } finally {
    if (showLoader) isLoading.value = false;
    if (manual) isRefreshing.value = false;
    if (!hasActiveImport.value) stopPolling();
  }
};

const changeSkipLogsType = async type => {
  if (type === selectedSkipLogsType.value || isChangingSkipLogsType.value) {
    return;
  }

  selectedSkipLogsType.value = type;
  isChangingSkipLogsType.value = true;
  try {
    await fetchImport({ requestedSkipLogsType: type });
  } finally {
    isChangingSkipLogsType.value = false;
  }
};

const refreshImportInBackground = async () => {
  if (
    !isPageActive ||
    isPolling.value ||
    !hasActiveImport.value ||
    document.hidden
  ) {
    return;
  }

  isPolling.value = true;
  try {
    await fetchImport();
  } finally {
    isPolling.value = false;
    if (!hasActiveImport.value) stopPolling();
  }
};

const startPolling = () => {
  stopPolling();
  if (!isPageActive || !hasActiveImport.value) return;

  pollTimer = window.setInterval(refreshImportInBackground, POLL_INTERVAL_MS);
};

const abandonImport = async () => {
  isAbandoning.value = true;
  try {
    const response = await DataImportsAPI.abandon(dataImport.value.id);
    dataImport.value = response.data;
    stopPolling();
    useAlert(t('DATA_IMPORTS.ALERTS.IMPORT_ABANDONED'));
  } finally {
    isAbandoning.value = false;
  }
};

const retryImport = async () => {
  isRetrying.value = true;
  importRequestVersion += 1;
  stopPolling();
  try {
    const response = await DataImportsAPI.retry(dataImport.value.id);
    dataImport.value = response.data;
    useAlert(t('DATA_IMPORTS.ALERTS.IMPORT_RETRIED'));
  } catch {
    useAlert(t('DATA_IMPORTS.ALERTS.IMPORT_RETRY_FAILED'));
  } finally {
    isRetrying.value = false;
    if (hasActiveImport.value) startPolling();
  }
};

const downloadCsv = (response, filename) => {
  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: 'text/csv' })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const downloadErrorLogs = async () => {
  isDownloadingErrorLogs.value = true;
  try {
    const response = await DataImportsAPI.downloadErrorLogs(
      dataImport.value.id
    );
    downloadCsv(response, `data-import-${dataImport.value.id}-error-logs.csv`);
  } finally {
    isDownloadingErrorLogs.value = false;
  }
};

const downloadSkipLogs = async () => {
  isDownloadingSkipLogs.value = true;
  try {
    const response = await DataImportsAPI.downloadSkipLogs(dataImport.value.id);
    downloadCsv(response, `data-import-${dataImport.value.id}-skip-logs.csv`);
  } finally {
    isDownloadingSkipLogs.value = false;
  }
};

const handleVisibilityChange = () => {
  if (isPageActive && !document.hidden && hasActiveImport.value) {
    refreshImportInBackground();
  }
};

onActivated(async () => {
  isPageActive = true;
  await fetchImport({ showLoader: true });
  if (!isPageActive) return;

  // Collapse empty sections by default; expand the ones with records.
  errorsOpen.value = Boolean(dataImport.value?.import_errors_count);
  skipLogsOpen.value = Boolean(dataImport.value?.skip_logs_count);
  startPolling();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onDeactivated(() => {
  isPageActive = false;
  stopPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
  isPageActive = false;
  stopPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<template>
  <SettingsPageShell wide :title="pageTitle">
    <ImportDetailHeader
      :data-import="dataImport"
      :is-refreshing="isRefreshing"
      :is-retrying="isRetrying"
      :is-abandoning="isAbandoning"
      :is-polling="isPolling"
      @refresh="fetchImport({ manual: true })"
      @retry="retryImport"
      @abandon="abandonImport"
    />

    <SettingsSection
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="$t('DATA_IMPORTS.LOADING')"
    >
      <div
        v-for="row in 4"
        :key="row"
        class="flex items-center gap-3 px-4 py-4"
      >
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <div v-else-if="dataImport" class="flex flex-col gap-3">
      <ImportSummaryTiles :data-import="dataImport" />

      <ImportProgress
        v-if="dataImport.import_types?.length"
        :data-import="dataImport"
        :title="$t('DATA_IMPORTS.DETAIL.PROGRESS')"
      />

      <ImportErrorsSection
        :data-import="dataImport"
        :is-open="errorsOpen"
        :is-downloading="isDownloadingErrorLogs"
        @toggle="errorsOpen = !errorsOpen"
        @download="downloadErrorLogs"
      />

      <ImportSkipLogsSection
        :data-import="dataImport"
        :is-open="skipLogsOpen"
        :is-downloading="isDownloadingSkipLogs"
        :selected-type="selectedSkipLogsType"
        :is-changing-type="isChangingSkipLogsType"
        @toggle="skipLogsOpen = !skipLogsOpen"
        @download="downloadSkipLogs"
        @change-type="changeSkipLogsType"
      />
    </div>
  </SettingsPageShell>
</template>
